import { differenceInYears, parseISO } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMe } from '@/api/hooks/useMe'
import { IndividualType } from '@/hooks/useIndividuals'

import { EditIndividualDialog } from './edit-individual-dialog'

type Props = {
  individual: IndividualType
}

export const IndividualInfoCard = ({ individual }: Props) => {
  const { t } = useTranslation()
  const { data: me } = useMe()
  const [editOpen, setEditOpen] = useState(false)

  const age = differenceInYears(new Date(), parseISO(individual.birthDate))

  const myEntry = individual.users.find(u => u.id === me?.id)

  const roles: string[] = []
  if (myEntry?.isGuardian) roles.push(t('homePage.infoCard.role.guardian'))
  if (myEntry?.isLegalRepresentative) roles.push(t('homePage.infoCard.role.legalRepresentative'))

  const rows: { label: string; value: string }[] = [
    {
      label: t('homePage.infoCard.label.name'),
      value: `${individual.firstName} ${individual.lastName}`,
    },
    { label: t('homePage.infoCard.label.age'), value: t('homePage.infoCard.age', { age }) },
    ...(individual.gender
      ? [
          {
            label: t('homePage.infoCard.label.gender'),
            value: t(`homePage.infoCard.gender.${individual.gender}`),
          },
        ]
      : []),
    { label: t('formInputs.municipality.label'), value: individual.municipality.name },
    ...(myEntry
      ? [
          {
            label: t('homePage.infoCard.label.myRelation', { firstName: individual.firstName }),
            value:
              myEntry.relation === 'other' && myEntry.relationOther
                ? myEntry.relationOther
                : t(`homePage.infoCard.relation.${myEntry.relation}`),
          },
        ]
      : []),
    ...(roles.length > 0
      ? [{ label: t('homePage.infoCard.label.myRole'), value: roles.join(' & ') }]
      : []),
  ]

  return (
    <section className="space-y-3">
      <h3 className="text-lg font-heading font-bold">{t('homePage.infoCard.heading')}</h3>
      <div className="card p-4">
        <div className="flex items-start justify-between gap-4">
          <dl className="flex flex-col gap-4">
            {rows.map(({ label, value }) => (
              <div className="flex flex-col gap-0.5" key={label}>
                <dt className="text-text-secondary text-sm">{label}</dt>
                <dd className="text-base font-medium">{value}</dd>
              </div>
            ))}
          </dl>
          <EditIndividualDialog
            individual={individual}
            onOpenChange={setEditOpen}
            open={editOpen}
          />
        </div>
      </div>
    </section>
  )
}

export default IndividualInfoCard
